import React from 'react';
import { Paperclip } from 'lucide-react';
import type { ThreadMessage } from '../../services/supportApi';
import { formatDate, formatShortDate } from './constants';
import { unwrapEmailText } from './v3/emailText';
import { Linkified } from './linkify';

// Email in testo semplice: righe riunite (a capo del client ogni ~70 caratteri).
const shownBody = (msg: ThreadMessage) => (msg.via === 'email' ? unwrapEmailText(msg.body) : msg.body);

interface ThreadViewProps {
  /** `operator`: bolle con nome operatore. `requester`: vista mascherata sul canale. */
  variant: 'operator' | 'requester';
  messages: ThreadMessage[];
  /** Apre un allegato (gestisce da sé il toast d'errore). */
  onOpenAttachment: (storedName: string, displayName?: string) => void;
  /** Testo quando il thread è vuoto. */
  emptyText?: string;
  /** Solo `requester`: tipo autore che corrisponde a «Tu». */
  mineAuthorType?: 'associate' | 'guest';
  /** Solo `requester`: nome del canale mostrato al posto dell'operatore. */
  channelName?: string;
}

// Thread pubblico del ticket. Le due varianti hanno markup distinto perché
// distinte sono le pagine: l'operatore vede la conversazione dentro la colonna
// sinistra del dettaglio, il richiedente una card a sé.
const ThreadView: React.FC<ThreadViewProps> = ({
  variant,
  messages,
  onOpenAttachment,
  emptyText,
  mineAuthorType = 'associate',
  channelName = 'UNASCA',
}) => {
  if (variant === 'requester') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
        {messages && messages.length > 0 ? (
          <div className="space-y-4">
            {messages.map((msg, idx) => {
              const isMine = msg.authorType === mineAuthorType;
              const senderLabel = isMine ? 'Tu' : channelName;
              return (
                <div key={idx} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5 px-1">
                    {senderLabel} · {formatShortDate(msg.sentAt)}
                  </p>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                      isMine
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-gray-800 dark:text-gray-200 rounded-tr-sm'
                        : 'bg-gray-100 dark:bg-gray-700/60 text-gray-800 dark:text-gray-200 rounded-tl-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap"><Linkified text={shownBody(msg)} /></p>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {msg.attachments.map((att, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => onOpenAttachment(att.url, att.filename)}
                            className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-700 dark:hover:text-indigo-300 max-w-full"
                          >
                            <Paperclip className="w-3 h-3 shrink-0" />
                            <span className="truncate">{att.filename}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
            {emptyText || 'Nessun messaggio nel thread'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 max-h-[28rem] overflow-y-auto">
      {messages.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
          {emptyText || 'Nessun messaggio.'}
        </p>
      )}
      {messages.map((msg, i) => {
        const isRep = msg.authorType === 'representative' || msg.authorType === 'user';
        return (
          <div key={i} className={`flex ${isRep ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                isRep
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
              }`}
            >
              {msg.authorName && (
                <div
                  className={`text-2xs mb-0.5 ${
                    isRep ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {msg.authorName}
                </div>
              )}
              <div className="whitespace-pre-wrap break-words"><Linkified text={shownBody(msg)} /></div>
              {msg.attachments && msg.attachments.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {msg.attachments.map((att, ai) => (
                    <button
                      key={ai}
                      type="button"
                      onClick={() => onOpenAttachment(att.url, att.filename)}
                      className={`flex items-center gap-1 text-2xs underline max-w-full ${
                        isRep
                          ? 'text-primary-100 hover:text-white'
                          : 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                      }`}
                    >
                      <Paperclip className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{att.filename}</span>
                    </button>
                  ))}
                </div>
              )}
              <div
                className={`text-2xs mt-1 ${
                  isRep ? 'text-primary-100' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {formatDate(msg.sentAt)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ThreadView;
